<script setup>
/*
 * SEO Analysis Component
 *
 * Usage:
 * <warnings-errors
 *   :blockEditorContent="content"
 *   :design="design"
 *   :pageTitle="pageTitle"
 *   :metaDescription="metaDescription"
 * />
 */

import {onMounted, ref, watch, nextTick, computed} from "vue";
import SlideoutWithSlots from '../elements/slideoutWithSlots.vue';
import {
    checkHeadlineLength,
    checkHeadlinePresence,
    checkHeadlineStructure,
    checkButtonLabel,
    checkButtonLink,
    checkMissingMedia,
    checkWCAGContrast,
    runComprehensiveSEOCheck,
} from "../../pageBuilderChecker";

const showingSlideout = ref(false);
const warnings = ref([]);
const errors = ref([]);
const seoLevel = ref('comprehensive');
const seoMetrics = ref({});
const seoSummary = ref({});

const mfChecks = ref([
    {
        message: 'SSL Certificate / HTTPS',
        category: 'Security',
        severity: 'mf'
    },
    {
        message: 'Use HTTP/2',
        category: 'Security',
        severity: 'mf'
    },
    {
        message: 'Mixed Content Fixer',
        category: 'Security',
        severity: 'mf'
    },
    {
        message: 'Server Side Caching',
        category: 'Performance',
        severity: 'mf'
    },
    {
        message: 'Preload Largest Contentful Paint image',
        category: 'Performance',
        severity: 'mf'
    },
    {
        message: 'Robots.txt',
        category: 'SEO',
        severity: 'mf'
    },
    {

    }
]);

const props = defineProps({
    blockEditorContent: {
        type: Object,
        required: true,
    },
    design: {
        type: Object,
        required: true,
    },
    pageTitle: {
        type: String,
        default: ''
    },
    metaDescription: {
        type: String,
        default: ''
    },
    containerType: {
        type: String,
        default: ''
    }
})

function checkAllWarnings() {
    warnings.value = [];

    // Run comprehensive SEO check
    const seoResults = runComprehensiveSEOCheck(
        props.blockEditorContent,
        props.design,
        mergedPageData.value,
        seoLevel.value
    );

    warnings.value.push(...seoResults.warnings);
    seoMetrics.value = seoResults.metrics;
    seoSummary.value = seoResults.summary;

    // Keep existing WCAG contrast check as it's separate from SEO
    const contrastCheck = checkWCAGContrast(props.blockEditorContent, props.design);
    warnings.value.push(...contrastCheck.warnings);

    filterCategories();
}

function checkAllErrors() {
    errors.value = [];

    const buttonCheck = checkButtonLabel(props.blockEditorContent);
    errors.value.push(...buttonCheck.warnings);

    const buttonLinkCheck = checkButtonLink(props.blockEditorContent);
    errors.value.push(...buttonLinkCheck.warnings);

    const missingMediaCheck = checkMissingMedia(props.blockEditorContent);
    errors.value.push(...missingMediaCheck.warnings);

    filterCategories();
}

function filterCategories() {
    var containerTypes = [
        'chapter',
        'template',
        'expert-contract',
        'customerarea',
        'app_page'
    ];

    if(containerTypes.includes(props.containerType)) {
        errors.value = errors.value.filter(e => e.category !== 'SEO');
        warnings.value = warnings.value.filter(w => w.category !== 'SEO');
    }
}

function scrollToElement(uuid) {
    const element = document.querySelector(`.mfpbb-${uuid}`);
    if (element) {
        element.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});

        showingSlideout.value = false;

        element.classList.add('wiggle');

        setTimeout(() => {
            element.classList.remove('wiggle');
        }, 2000);
    }
}

function getSeverityIcon(severity) {
    switch (severity) {
        case 'high': return 'fa-circle-exclamation text-red-600';
        case 'medium': return 'fa-triangle-exclamation text-yellow-600';
        case 'low': return 'fa-circle-info text-blue-600';
        case 'mf': return 'fa-check-circle text-green-600';
        default: return 'fa-circle-info text-yellow-600';
    }
}

function getSeverityColor(severity) {
    switch (severity) {
        case 'high': return 'bg-red-50 border-red-200';
        case 'medium': return 'bg-yellow-50 border-yellow-200';
        case 'low': return 'bg-blue-50 border-blue-200';
        default: return 'bg-gray-50 border-gray-200';
    }
}

function changeSeoLevel(level) {
    seoLevel.value = level;
    checkAllWarnings();
}

const mergedPageData = computed(() => {
    return {
        title: props.pageTitle || '',
        description: props.metaDescription || ''
    };
});

const criticalWarnings = computed(() => warnings.value.filter(w => w.severity === 'high'));
const mediumWarnings = computed(() => warnings.value.filter(w => w.severity === 'medium'));
const lowWarnings = computed(() => warnings.value.filter(w => w.severity === 'low' || !w.severity));

watch(() => props.blockEditorContent, () => {
    checkAllWarnings();
    checkAllErrors();
}, { immediate: true, deep: true });



watch(() => props.pageTitle, () => {
    checkAllWarnings();
});

watch(() => props.metaDescription, () => {
    checkAllWarnings();
});

onMounted(() => {
    checkAllWarnings();
    checkAllErrors();
});
</script>

<template>
    <div>

        <div @click="showingSlideout = true" class="flex items-center cursor-pointer">
            <div class="flex items-center mr-3" v-if="lowWarnings.length > 0">
                <div class="mr-1 text-xl text-blue-600 fa-solid fa-circle-info"></div>
                <div class="text-sm font-medium">{{ lowWarnings.length }}</div>
            </div>

            <div class="flex items-center mr-3" v-if="mediumWarnings.length > 0">
                <div class="mr-1 text-xl text-yellow-600 fa-solid fa-triangle-exclamation"></div>
                <div class="text-sm font-medium">{{ mediumWarnings.length }}</div>
            </div>

            <div class="flex items-center mr-3" v-if="errors.length > 0">
                <div class="mr-1 text-xl text-red-600 fa-solid fa-times-circle"></div>
                <div class="text-sm font-medium">{{ errors.length }}</div>
            </div>

            <div class="flex items-center mr-3" v-if="criticalWarnings.length > 0">
                <div class="mr-1 text-xl text-red-600 fa-solid fa-circle-exclamation"></div>
                <div class="text-sm font-medium">{{ criticalWarnings.length }}</div>
            </div>
        </div>

        <slideout-with-slots v-if="showingSlideout" @close="showingSlideout = false">
            <template #title>
                <h5 class="text-lg font-medium">Analysis & Errors</h5>
            </template>
            <template #body>
                <div>
                    <div class="p-4 mb-6 border border-gray-200 rounded-lg bg-gray-50">
                        <h6 class="mb-3 text-sm font-medium">Page Information & Metrics</h6>

                        <div class="p-3 mb-4 bg-white border rounded">
                            <div class="mb-2 text-xs font-medium text-gray-600">Meta Information</div>
                            <div v-if="mergedPageData.title" class="mb-1 text-sm">
                                <span class="text-gray-600">Title:</span>
                                <span class="ml-1 font-medium">"{{ mergedPageData.title }}"</span>
                                <span class="ml-2 text-xs text-gray-500">({{ mergedPageData.title.length }} chars)</span>
                            </div>
                            <div v-else class="mb-1 text-sm text-gray-400">
                                <span class="text-gray-600">Title:</span>
                                <em class="ml-1">Not Added</em>
                            </div>
                            <div v-if="mergedPageData.description" class="text-sm">
                                <span class="text-gray-600">Description:</span>
                                <span class="ml-1 font-medium">"{{ mergedPageData.description }}"</span>
                                <span class="ml-2 text-xs text-gray-500">({{ mergedPageData.description.length }} chars)</span>
                            </div>
                            <div v-else class="text-sm text-gray-400">
                                <span class="text-gray-600">Description:</span>
                                <em class="ml-1">Not Added</em>
                            </div>
                        </div>

                        <div v-if="Object.keys(seoMetrics).length > 0" class="grid grid-cols-2 gap-4 text-sm">
                            <div v-if="seoMetrics.wordCount">
                                <span class="text-gray-600">Word Count:</span>
                                <span class="ml-1 font-medium">{{ seoMetrics.wordCount }}</span>
                            </div>
                            <div v-if="seoMetrics.totalLinks">
                                <span class="text-gray-600">Total Links:</span>
                                <span class="ml-1 font-medium">{{ seoMetrics.totalLinks }}</span>
                            </div>
                            <div v-if="seoMetrics.ctaCount">
                                <span class="text-gray-600">Call-to-Actions:</span>
                                <span class="ml-1 font-medium">{{ seoMetrics.ctaCount }}</span>
                            </div>
                            <div v-if="seoMetrics.internalLinks !== undefined">
                                <span class="text-gray-600">Internal Links:</span>
                                <span class="ml-1 font-medium">{{ seoMetrics.internalLinks }}</span>
                            </div>
                            <div v-if="seoMetrics.externalLinks !== undefined">
                                <span class="text-gray-600">External Links:</span>
                                <span class="ml-1 font-medium">{{ seoMetrics.externalLinks }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="errors.length > 0" class="mb-6">
                        <h6 class="mb-3 text-sm font-medium text-red-600">Errors (Must Fix)</h6>
                        <div v-for="error in errors" :key="error.message" class="p-3 mb-3 border border-red-200 rounded-lg bg-red-50">
                            <div class="flex items-center">
                                <i class="mr-2 text-xl text-red-600 fa-solid fa-times-circle"></i>
                                <div class="flex-1">
                                    {{ error.message }}
                                    <div v-if="error.uuid" class="flex items-center">
                                        <br><span class="mf-link" @click="scrollToElement(error.uuid)">Scroll to element</span>
                                    </div>
                                </div>
                                <pill class="ml-4 text-black bg-gray-200">
                                    {{ error.category }}
                                </pill>
                            </div>
                        </div>
                    </div>

                    <div v-if="criticalWarnings.length > 0" class="mb-6">
                        <h6 class="mb-3 text-sm font-medium text-red-600">Issues</h6>
                        <div v-for="warning in criticalWarnings" :key="warning.message" :class="['p-3 mb-3 rounded-lg border', getSeverityColor(warning.severity)]">
                            <div class="flex items-center">
                                <i :class="['mr-2 text-xl fa-solid', getSeverityIcon(warning.severity)]"></i>
                                <div class="flex-1">
                                    {{ warning.message }}
                                    <div v-if="warning.uuid" class="flex items-center">
                                        <br><span class="mf-link" @click="scrollToElement(warning.uuid)">Scroll to element</span>
                                    </div>
                                </div>
                                <pill class="ml-4 text-black bg-gray-200">
                                    {{ warning.category }}
                                </pill>
                            </div>
                        </div>
                    </div>

                    <div v-if="mediumWarnings.length > 0" class="mb-6">
                        <h6 class="mb-3 text-sm font-medium text-yellow-600">Tasks</h6>
                        <div v-for="warning in mediumWarnings" :key="warning.message" :class="['p-3 mb-3 rounded-lg border', getSeverityColor(warning.severity)]">
                            <div class="flex items-center">
                                <i :class="['mr-2 text-xl fa-solid', getSeverityIcon(warning.severity)]"></i>
                                <div class="flex-1">
                                    {{ warning.message }}
                                    <div v-if="warning.uuid" class="flex items-center">
                                        <br><span class="mf-link" @click="scrollToElement(warning.uuid)">Scroll to element</span>
                                    </div>
                                </div>
                                <pill class="ml-4 text-black bg-gray-200">
                                    {{ warning.category }}
                                </pill>
                            </div>
                        </div>
                    </div>

                    <div v-if="lowWarnings.length > 0" class="mb-6">
                        <h6 class="mb-3 text-sm font-medium text-blue-600">Suggestions</h6>
                        <div v-for="warning in lowWarnings" :key="warning.message" :class="['p-3 mb-3 rounded-lg border', getSeverityColor(warning.severity)]">
                            <div class="flex items-center">
                                <i :class="['mr-2 text-xl fa-solid', getSeverityIcon(warning.severity)]"></i>
                                <div class="flex-1">
                                    {{ warning.message }}
                                    <div v-if="warning.uuid" class="flex items-center">
                                        <br><span class="mf-link" @click="scrollToElement(warning.uuid)">Scroll to element</span>
                                    </div>
                                </div>
                                <pill class="ml-4 text-black bg-gray-200">
                                    {{ warning.category }}
                                </pill>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h6 class="mb-3 text-sm font-medium text-green-700">Always done by <i>myflow</i></h6>
                        <div v-for="check in mfChecks" :key="check.message" :class="['p-3 mb-3 rounded-lg border', getSeverityColor(check.severity)]">
                            <div class="flex items-center">
                                <i :class="['mr-2 text-xl fa-solid', getSeverityIcon(check.severity)]"></i>
                                <div class="flex-1">
                                    {{ check.message }}
                                    <div v-if="check.uuid" class="flex items-center">
                                        <br><span class="mf-link" @click="scrollToElement(check.uuid)">Scroll to element</span>
                                    </div>
                                </div>
                                <pill class="ml-4 text-black bg-gray-200">
                                    {{ check.category }}
                                </pill>
                            </div>
                        </div>
                    </div>

                    <div v-if="warnings.length === 0 && errors.length === 0" class="p-6 text-center rounded-lg bg-green-50">
                        <i class="mb-2 text-3xl text-green-600 fa-solid fa-check-circle"></i>
                        <p class="font-medium text-green-800">Great job! No SEO issues found.</p>
                        <p class="mt-1 text-sm text-green-600">Your page follows SEO best practices.</p>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="text-xs text-center text-gray-500">
                    SEO analysis helps improve your page's search engine visibility and user experience.
                </div>
            </template>
        </slideout-with-slots>
    </div>
</template>

<style scoped>
</style>
